<template>
  <div class="complaint-record-page">
    <div class="crumbs">
      <el-breadcrumb separator="/">
        <el-breadcrumb-item>首页</el-breadcrumb-item>
        <el-breadcrumb-item>客诉处理</el-breadcrumb-item>
        <el-breadcrumb-item>客诉处理记录</el-breadcrumb-item>
      </el-breadcrumb>
    </div>
    <div class="container">
      <h3 class="filter-title">查询列表</h3>
      <el-form :inline="true" size="small" @submit.native.prevent="search">
        <el-form-item label="查询反馈时间">
          <el-date-picker v-model="filters.dates" type="daterange" value-format="yyyy-MM-dd"
            range-separator="至" start-placeholder="开始日期" end-placeholder="结束日期"
            :picker-options="dateOptions" :disabled="loading" />
        </el-form-item>
        <el-form-item label="问题类型">
          <el-select v-model="filters.issue_types" multiple clearable collapse-tags placeholder="全部问题" :disabled="loading">
            <el-option v-for="item in issueTypes" :key="item.value" :label="item.label" :value="item.value" />
          </el-select>
        </el-form-item>
        <el-form-item label="处理状态">
          <el-select v-model="filters.process_statuses" multiple clearable collapse-tags placeholder="全部状态" :disabled="loading">
            <el-option v-for="item in processStatuses" :key="item.value" :label="item.label" :value="item.value" />
          </el-select>
        </el-form-item>
        <el-form-item label="问题详情">
          <el-input v-model.trim="filters.keyword" clearable maxlength="300" placeholder="请输入问题详情" :disabled="loading" />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" icon="el-icon-search" :loading="loading" @click="search">搜索</el-button>
          <el-button type="warning" icon="el-icon-refresh" :disabled="loading" @click="resetQuery">重置</el-button>
        </el-form-item>
      </el-form>
      <div class="table-toolbar"><strong>客诉处理记录</strong></div>
      <el-table v-loading="loading" :data="rows" border size="small" empty-text="暂无客诉处理记录">
        <el-table-column prop="record_no" label="记录 ID" width="180" />
        <el-table-column prop="feedback_time" label="反馈时间" width="165" />
        <el-table-column label="问题类型" width="140">
          <template slot-scope="scope">{{ scope.row.issue_type_name || issueTypeLabel(scope.row.issue_type) }}</template>
        </el-table-column>
        <el-table-column label="问题详情" min-width="260">
          <template slot-scope="scope">
            <div class="cell-summary">{{ scope.row.issue_summary || '—' }}</div>
            <el-button type="text" size="mini" :disabled="detailLoading" @click="openDetail(scope.row, 'issue')">查看</el-button>
          </template>
        </el-table-column>
        <el-table-column label="处理状态" width="165">
          <template slot-scope="scope">
            <el-tag size="small" :type="processTagType(scope.row.process_status)">{{ processLabel(scope.row.process_status) }}</el-tag>
            <small v-if="syncLabel(scope.row)" class="sync-note" :class="{ 'sync-failed': Number(scope.row.item_provision_sync_status) === 4 }">{{ syncLabel(scope.row) }}</small>
          </template>
        </el-table-column>
        <el-table-column label="AI 处理结果" min-width="280">
          <template slot-scope="scope">
            <div class="cell-summary">{{ aiSummary(scope.row) || '—' }}</div>
            <el-button v-if="aiSummary(scope.row)" type="text" size="mini" :disabled="detailLoading" @click="openDetail(scope.row, 'ai')">查看</el-button>
          </template>
        </el-table-column>
        <el-table-column prop="target_group_name" label="飞书群" min-width="180" show-overflow-tooltip />
        <el-table-column label="企微提交人" min-width="190" show-overflow-tooltip>
          <template slot-scope="scope">{{ submitterLabel(scope.row) }}</template>
        </el-table-column>
        <el-table-column label="飞书发送账号" min-width="170" show-overflow-tooltip>
          <template slot-scope="scope">{{ senderLabel(scope.row) }}</template>
        </el-table-column>
        <el-table-column label="发送状态" width="100">
          <template slot-scope="scope">
            <el-tag size="small" :type="sendTagType(scope.row.send_status)">{{ sendLabel(scope.row.send_status) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="飞书消息 ID" min-width="220" show-overflow-tooltip>
          <template slot-scope="scope">{{ scope.row.message_id || '—' }}</template>
        </el-table-column>
        <el-table-column label="失败原因" min-width="240" show-overflow-tooltip>
          <template slot-scope="scope">{{ failureLabel(scope.row) }}</template>
        </el-table-column>
      </el-table>
      <div class="pagination">
        <el-pagination background :current-page="query.page" :page-size="query.limit" :total="total"
          :page-sizes="[20, 50, 100]" layout="total, sizes, prev, pager, next" :disabled="loading"
          @current-change="changePage" @size-change="changeSize" />
      </div>
    </div>
    <el-dialog :title="detailTitle" :visible.sync="dialogVisible" width="760px" :close-on-click-modal="false">
      <div v-loading="detailLoading" class="detail-body"><pre class="detail-copy">{{ detailText || '—' }}</pre></div>
      <span slot="footer"><el-button @click="dialogVisible = false">关闭</el-button></span>
    </el-dialog>
  </div>
</template>

<script>
import dayjs from 'dayjs';
import { getComplaintRecords, getComplaintRecord } from '@/api/complaintFeedbackRecord';

const issueTypes = [
  { value: 1, label: 'boss 道具未下发' }, { value: 2, label: '重复充值' },
  { value: 3, label: '门票使用异常' }, { value: 4, label: '举报脚本' }, { value: 5, label: '其他' },
];
const processStatuses = [
  { value: 0, label: '待处理' }, { value: 1, label: '已核实' }, { value: 2, label: '已解决' },
  { value: 3, label: '处理异常' }, { value: 4, label: '转人工' },
];
const defaultFilters = () => ({ dates: [dayjs().subtract(6, 'day').format('YYYY-MM-DD'), dayjs().format('YYYY-MM-DD')], issue_types: [], process_statuses: [], keyword: '' });
function formatAi(record, suffix = '') {
  return [['ai_suggestion', '处理建议'], ['ai_result', '结果'], ['ai_details', '明细']]
    .filter(([key]) => record[key + suffix])
    .map(([key, label]) => `${label}：${record[key + suffix]}`).join('\n');
}

export default {
  name: 'complaintFeedbackRecords',
  data() {
    return {
      loading: false, detailLoading: false, rows: [], total: 0, filters: defaultFilters(),
      query: { page: 1, limit: 20 }, issueTypes, processStatuses,
      dialogVisible: false, detailTitle: '', detailText: '', detailRequestId: 0,
      dateOptions: { shortcuts: [
        { text: '近 7 天', onClick: picker => picker.$emit('pick', [dayjs().subtract(6, 'day').toDate(), dayjs().toDate()]) },
        { text: '今天', onClick: picker => picker.$emit('pick', [dayjs().toDate(), dayjs().toDate()]) },
        { text: '近 30 天', onClick: picker => picker.$emit('pick', [dayjs().subtract(29, 'day').toDate(), dayjs().toDate()]) },
      ] },
    };
  },
  created() { this.search(); },
  beforeDestroy() { this.detailRequestId += 1; },
  methods: {
    showError(error) {
      if (error instanceof Error && !error.response && !error.config) this.$message.error(error.message);
    },
    async loadList() {
      if (this.loading) return;
      this.loading = true;
      try {
        const data = await getComplaintRecords({ ...this.query });
        this.rows = data.data || []; this.total = Number(data.total || 0);
      } catch (error) { this.showError(error); }
      finally { this.loading = false; }
    },
    search() {
      if (this.loading) return;
      // 清空日期恢复默认近7天，与后端缺省行为保持一致。
      const dates = this.filters.dates || defaultFilters().dates;
      if (dates.length !== 2 || !dates[0] || !dates[1] || dates[0] > dates[1]) {
        this.$message.error('请选择有效的反馈时间范围，开始日期不得晚于结束日期'); return;
      }
      this.filters.dates = dates;
      this.query = {
        page: 1, limit: this.query.limit, start_date: dates[0], end_date: dates[1],
        issue_types: [...this.filters.issue_types], process_statuses: [...this.filters.process_statuses], keyword: this.filters.keyword.trim(),
      };
      return this.loadList();
    },
    resetQuery() { if (this.loading) return; this.filters = defaultFilters(); return this.search(); },
    changePage(page) { if (this.loading) return; this.query.page = page; return this.loadList(); },
    changeSize(limit) { if (this.loading) return; this.query.limit = limit; this.query.page = 1; return this.loadList(); },
    async openDetail(row, type) {
      if (this.detailLoading) return;
      const requestId = ++this.detailRequestId;
      this.detailTitle = type === 'issue' ? '问题详情' : 'AI 处理结果';
      this.detailText = ''; this.dialogVisible = true; this.detailLoading = true;
      try {
        const data = await getComplaintRecord(row.id);
        if (requestId === this.detailRequestId) this.detailText = type === 'issue' ? data.issue_details : formatAi(data);
      } catch (error) {
        if (requestId === this.detailRequestId) { this.dialogVisible = false; this.showError(error); }
      } finally { if (requestId === this.detailRequestId) this.detailLoading = false; }
    },
    aiSummary(row) { return formatAi(row, '_summary'); },
    issueTypeLabel(value) { const item = issueTypes.find(item => item.value === Number(value)); return item ? item.label : '未知类型'; },
    processLabel(value) { const item = processStatuses.find(item => item.value === Number(value)); return item ? item.label : '未知状态'; },
    processTagType(value) { return ({ 0: 'info', 1: 'success', 2: 'success', 3: 'danger', 4: 'warning' })[Number(value)] || 'info'; },
    sendLabel(value) { return ({ 0: '待发送', 1: '成功', 2: '失败', 3: '发送中' })[Number(value)] || '未知状态'; },
    sendTagType(value) { return ({ 0: 'info', 1: 'success', 2: 'danger', 3: 'warning' })[Number(value)] || 'info'; },
    submitterLabel(row) { return [row.wecom_submitter, row.wecom_submitter_name].filter(Boolean).join(' / ') || '—'; },
    senderLabel(row) { return Number(row.feishu_sender_type) === 2 ? '应用机器人（兜底）' : (row.feishu_sender_name || '—'); },
    failureLabel(row) { return Number(row.send_status) === 2 ? ([row.failure_code, row.failure_reason].filter(Boolean).join('：') || '—') : '—'; },
    syncLabel(row) {
      if (![1, 2, 3].includes(Number(row.issue_type))) return '';
      return ({ 1: '待同步物品补发申请', 2: '正在同步物品补发申请', 3: '已同步物品补发申请', 4: '物品补发申请同步失败' })[Number(row.item_provision_sync_status)] || '';
    },
  },
};
</script>

<style scoped>
.filter-title { font-size: 14px; margin-bottom: 20px; }
.table-toolbar { margin: 0 0 16px; }
.cell-summary { overflow: hidden; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; white-space: pre-line; word-break: break-all; }
.sync-note { display: block; margin-top: 6px; color: #909399; line-height: 1.5; }
.sync-failed { color: #f56c6c; }
.pagination { text-align: right; margin-top: 20px; }
.detail-body { min-height: 120px; max-height: 60vh; overflow: auto; }
.detail-copy { white-space: pre-wrap; overflow-wrap: anywhere; word-break: break-all; font-family: inherit; line-height: 1.8; margin: 0; }
</style>
